<template>
  <div class="pp-cols" data-test="pp-tab-overview">
    <div class="pp-main">
      <PanelCard v-if="appointment" title="This appointment" icon="fa-calendar" test-id="pp-this-appointment">
        <dl class="pp-kv pp-kv--left">
          <dt>When</dt><dd>{{ whenText }}</dd>
          <dt v-if="meta.opName">Chair</dt><dd v-if="meta.opName">{{ meta.opName }}</dd>
          <dt v-if="meta.providerName">Provider</dt><dd v-if="meta.providerName">{{ meta.providerName }}</dd>
          <dt v-if="meta.statusText">Status</dt><dd v-if="meta.statusText">{{ meta.statusText }}</dd>
        </dl>
        <ul v-if="procedures.length" class="pp-procs">
          <li v-for="(p, i) in procedures" :key="i">{{ p }}</li>
        </ul>
        <p v-if="appointment.Note" class="pp-note"><span class="pp-note__lbl">Appointment note</span>{{ appointment.Note }}</p>
      </PanelCard>

      <PanelCard
        title="Unscheduled treatment"
        icon="fa-stethoscope"
        :count="unscheduled.length || '—'"
        :table="unscheduled.length > 0"
        test-id="pp-unscheduled"
      >
        <table v-if="unscheduled.length" class="pp-tbl">
          <thead><tr><th>Pri</th><th>Code</th><th>Procedure</th><th>Tooth</th><th>Planned</th></tr></thead>
          <tbody>
            <tr v-for="(t, i) in unscheduledShown" :key="i">
              <td :class="t.Priority ? 'pp-tbl__pri' : 'pp-tbl__pri--none'">{{ t.Priority || '—' }}</td>
              <td class="pp-tbl__code">{{ t.ProcCode }}</td>
              <td>{{ t.Descript }}</td>
              <td>{{ tooth(t) }}</td>
              <td class="pp-tbl__num">{{ date(t.DateTP) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="pp-empty">No unscheduled treatment.</p>
        <div v-if="unscheduled.length > LIMIT" class="pp-more pp-more--pad">
          Showing 1–{{ LIMIT }} of {{ unscheduled.length }} ·
          <button type="button" class="pp-textbtn" data-test="pp-unscheduled-all" @click="$emit('tab', 'treatment')">View all</button>
        </div>
      </PanelCard>

      <!--
        Design 1a priority card. Only when this source loads follow-ups (the page does; the panel not yet).
        A host fills #followups with its own rows (the page draws the editable queue); then this card
        is a table card and draws the "Showing 1–N of M" line itself. Unfilled, the read-only
        FollowupList draws the rows, its empty text and its View all.
      -->
      <PanelCard
        v-if="followups"
        title="Follow-ups"
        icon="fa-bell-o"
        :count="followups.length || '—'"
        :table="hostDrawsFollowups() && followups.length > 0"
        test-id="pp-overview-followups"
      >
        <p v-if="hostDrawsFollowups() && !followups.length" class="pp-empty">No open follow-ups for this patient.</p>
        <slot v-else name="followups" :limit="LIMIT">
          <FollowupList :rows="followups" :limit="LIMIT" :date-format="dateFormat" @view-all="$emit('tab', 'followups')" />
        </slot>
        <div v-if="hostDrawsFollowups() && followups.length > LIMIT" class="pp-more pp-more--pad">
          Showing 1–{{ LIMIT }} of {{ followups.length }} ·
          <button type="button" class="pp-textbtn" data-test="pp-followups-all" @click="$emit('tab', 'followups')">View all</button>
        </div>
      </PanelCard>

      <PanelCard
        title="Form submissions"
        icon="fa-file-text-o"
        :count="submissions.length ? `${submissions.length} total` : '—'"
        :table="submissions.length > 0"
        test-id="pp-overview-forms"
      >
        <table v-if="submissions.length" class="pp-tbl pp-tbl--narrow">
          <tbody>
            <tr v-for="s in submissionsShown" :key="s.FormSubmissionIDForWeb">
              <td>
                <a :href="submissionHref(s.FormSubmissionIDForWeb)" target="_blank" rel="noopener" class="pp-link">{{ s.Name || 'Form' }}</a>
              </td>
              <td class="pp-tbl__num">{{ date(s.DateUpdated) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="pp-empty">No forms submitted yet.</p>
        <div v-if="submissions.length > LIMIT" class="pp-more pp-more--pad">
          Showing 1–{{ LIMIT }} of {{ submissions.length }} ·
          <button type="button" class="pp-textbtn" data-test="pp-forms-all" @click="$emit('tab', 'forms')">View all</button>
        </div>
      </PanelCard>
    </div>

    <div class="pp-side">
      <PanelCard title="Snapshot" icon="fa-user" test-id="pp-snapshot">
        <dl class="pp-kv">
          <dt>Balance</dt>
          <dd :class="{ 'pp-kv__bad': estBalance > 0 }">{{ money(estBalance) }}</dd>
          <dt>Recall</dt><dd>{{ recallText }}</dd>
          <dt>Upcoming visits</dt><dd>{{ upcomingCount }}</dd>
          <dt v-if="patient && patient.Language">Language</dt>
          <dd v-if="patient && patient.Language">{{ patient.Language }}</dd>
        </dl>
      </PanelCard>

      <PanelCard title="Insurance" icon="fa-shield" test-id="pp-insurance">
        <template v-if="insurance">
          <div v-for="(plan, i) in plans" :key="i" class="pp-plan">
            <p class="pp-plan__title">{{ i === 0 ? 'Primary' : 'Secondary' }} · {{ plan.carrier || 'Carrier not recorded' }}</p>
            <dl class="pp-kv">
              <dt>Member ID</dt><dd class="pp-mono">{{ plan.memberId || '—' }}</dd>
            </dl>
          </div>
          <p v-if="!plans.length" class="pp-empty pp-empty--left">No insurance on file.</p>
          <dl class="pp-kv">
            <dt>Last verified</dt>
            <dd>
              <span v-if="verifiedOn" class="pp-pill" :class="insurance.isStale ? 'pp-pill--amber' : 'pp-pill--green'">{{ verifiedOn }}</span>
              <span v-else class="pp-pill pp-pill--rose">Never verified</span>
            </dd>
          </dl>
        </template>
        <p v-else-if="insuranceLoading" class="pp-empty pp-empty--left">Loading…</p>
        <p v-else class="pp-empty pp-empty--left" data-test="pp-insurance-off">
          Insurance details come from Insurance Verification, which is not switched on for this office.
        </p>
      </PanelCard>

      <PanelCard title="Contact & scheduling" icon="fa-address-card-o" test-id="pp-contact">
        <dl class="pp-kv">
          <dt>Next visit</dt><dd data-test="pp-next-visit">{{ nextVisitText }}</dd>
          <template v-for="l in phones">
            <dt :key="`k-${l.label}`">{{ l.label }}</dt><dd :key="`v-${l.label}`">{{ l.value }}</dd>
          </template>
          <dt v-if="patient && patient.Email">Email</dt>
          <dd v-if="patient && patient.Email" class="pp-wrap">{{ patient.Email }}</dd>
          <dt v-if="place">Lives in</dt><dd v-if="place">{{ place }}</dd>
          <dt>Texting</dt>
          <dd :class="{ 'pp-kv__bad': texting.blocked }" data-test="pp-texting">
            {{ texting.blocked ? texting.message : 'Texts allowed' }}
          </dd>
        </dl>
      </PanelCard>
    </div>
  </div>
</template>

<script>
/**
 * Overview — the design's OverviewTab (layout 1A), minus what has no data source yet (recent
 * visits with production, referral source) and plus Contact, which Patient Profile shows and the
 * design folded into the header.
 *
 * Host slot, scoped:
 *   #followups="{ limit }"  the Follow-ups card's rows. The page fills it with the editable queue
 *                           (its first `limit` rows); the Schedule View panel does not, so the card
 *                           keeps the read-only FollowupList there.
 *
 * ⛔ No prop named `patNum`, `siteID` or `loading` — mixin.js computeds silently replace them.
 */
import PanelCard from '../PanelCard.vue';
import FollowupList from '../FollowupList.vue';
import {
  formatOdDate, formatOdDateTime, money, phoneLines, procedureItems, toothLabel,
} from '@/utils/patientPanel/format';
import { getTextBlockReason } from '@/utils/textConsent';

export default {
  name: 'OverviewTab',

  components: { PanelCard, FollowupList },

  props: {
    /** The /schedule-view/patient payload. */
    payload: { type: Object, default: () => ({}) },
    appointment: { type: Object, default: null },
    appointmentMeta: { type: Object, default: null },
    insurance: { type: Object, default: null },
    insuranceLoading: { type: Boolean, default: false },
    /** upcomingFollowups rows, or null when this source does not load them (the panel today). */
    followups: { type: Array, default: null },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    timezone: { type: String, default: '' },
    today: { type: String, default: '' },
  },

  data() {
    // Design 1a: a priority card shows five rows, then "Showing 1–5 of N · View all".
    return { LIMIT: 5 };
  },

  computed: {
    patient() {
      return (this.payload && this.payload.patient) || null;
    },
    meta() {
      return this.appointmentMeta || {};
    },
    unscheduled() {
      return (this.payload && this.payload.unscheduledTx) || [];
    },
    unscheduledShown() {
      return this.unscheduled.slice(0, this.LIMIT);
    },
    /** Newest first — the classic page sorts the same way (by DateUpdated, descending). */
    submissions() {
      return ((this.payload && this.payload.formSubmissions) || [])
        .slice()
        .sort((a, b) => String(b.DateUpdated || '').localeCompare(String(a.DateUpdated || '')));
    },
    submissionsShown() {
      return this.submissions.slice(0, this.LIMIT);
    },
    /** The soonest scheduled visit from today on. OD datetimes sort as strings. */
    nextVisitText() {
      const next = ((this.payload && this.payload.scheduled) || [])
        .slice()
        .sort((a, b) => String(a.AptDateTime).localeCompare(String(b.AptDateTime)))[0];
      if (!next) return 'None scheduled';
      const when = formatOdDateTime(next.AptDateTime, this.dateFormat);
      const what = procedureItems(next.ProcDescript)[0] || '';
      return [what, when].filter(Boolean).join(' · ') || 'Scheduled';
    },
    procedures() {
      return procedureItems(this.appointment && this.appointment.ProcDescript);
    },
    whenText() {
      return formatOdDateTime(this.appointment && this.appointment.AptDateTime, this.dateFormat);
    },
    estBalance() {
      const n = Number(this.patient && this.patient.EstBalance);
      return Number.isFinite(n) ? n : 0;
    },
    recallText() {
      const d = this.patient && formatOdDate(this.patient.RecallScheduled, this.dateFormat);
      return d ? `Scheduled ${d}` : 'Not scheduled';
    },
    upcomingCount() {
      return ((this.payload && this.payload.scheduled) || []).length;
    },
    plans() {
      return ((this.insurance && this.insurance.plans) || []).map(p => ({
        carrier: p && p.insurance && p.insurance.carrierName,
        memberId: p && p.insurance && p.insurance.memberId,
      }));
    },
    verifiedOn() {
      if (!this.insurance || this.insurance.neverVerified) return '';
      return formatOdDate(this.insurance.lastVerified, this.dateFormat);
    },
    phones() {
      return phoneLines(this.patient);
    },
    place() {
      const p = this.patient || {};
      return [p.City, p.State].filter(Boolean).join(', ');
    },
    /** The ONE opt-out rule every send surface uses — see src/utils/textConsent.js. */
    texting() {
      return getTextBlockReason({ patient: this.patient || {}, timezone: this.timezone || undefined });
    },
  },

  methods: {
    money,
    tooth: toothLabel,
    /** True when the host fills #followups. A method, not a computed: $scopedSlots is not reactive. */
    hostDrawsFollowups() {
      return !!this.$scopedSlots.followups;
    },
    date(v) {
      return formatOdDate(v, this.dateFormat) || '—';
    },
    submissionHref(id) {
      const path = `/app/forms/view-submission/${encodeURIComponent(id)}`;
      return this.$router ? this.$router.resolve({ path }).href : path;
    },
  },
};
</script>
